import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import translations, { DEFAULT_LOCALE, SUPPORTED_LOCALES } from "./translations";

const I18nContext = createContext(null);
const STORAGE_KEY = "vatcalc.locale";

function get(obj, path) {
    return path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
}

function interpolate(str, vars) {
    if (typeof str !== "string" || !vars) return str;
    return str.replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : ""));
}

function detectInitialLocale() {
    if (typeof window === "undefined") return DEFAULT_LOCALE;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED_LOCALES.includes(stored)) return stored;
    const nav = (window.navigator?.language || "").slice(0, 2).toLowerCase();
    if (SUPPORTED_LOCALES.includes(nav)) return nav;
    return DEFAULT_LOCALE;
}

export function I18nProvider({ children }) {
    const [locale, setLocaleState] = useState(detectInitialLocale);

    const setLocale = useCallback((next) => {
        if (!SUPPORTED_LOCALES.includes(next)) return;
        setLocaleState(next);
        try {
            window.localStorage.setItem(STORAGE_KEY, next);
            document.documentElement.lang = next;
        } catch (e) {
            // ignore
        }
    }, []);

    useEffect(() => {
        document.documentElement.lang = locale;
    }, [locale]);

    const value = useMemo(() => {
        const dict = translations[locale] || translations[DEFAULT_LOCALE];
        const t = (path, vars) => {
            const v = get(dict, path);
            if (v == null) return get(translations[DEFAULT_LOCALE], path) ?? path;
            return interpolate(v, vars);
        };
        const tRaw = (path) => get(dict, path) ?? get(translations[DEFAULT_LOCALE], path);
        return { locale, setLocale, t, tRaw, locales: SUPPORTED_LOCALES, meta: dict.meta };
    }, [locale, setLocale]);

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
    const ctx = useContext(I18nContext);
    if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
    return ctx;
}

export function useT() {
    return useI18n().t;
}
